import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  BarChart3,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  Building2,
  MapPin,
  Radio,
  Download,
  Calendar,
  Loader2,
  PieChart,
  Activity,
  Shield,
  Target,
  FileText,
  AlertCircle
} from 'lucide-react';
import { api } from '@/utils/api';

interface AnalyticsData {
  totalAudits: number;
  completedAudits: number;
  avgScore: number;
  passRate: number;
  conditionalPassRate: number;
  failRate: number;
  totalFindings: number;
  openFindings: number;
  criticalFindings: number;
  byStatus: Record<string, number>;
  byRegion: Record<string, number>;
  byMarket: Record<string, number>;
  byCarrier: Record<string, number>;
  byAuditor: Record<string, number>;
  recentTrend: { period: string; count: number; avgScore: number }[];
}

interface QuarterlyReport {
  quarter: string;
  year: number;
  summary: {
    totalAudits: number;
    avgScore: number;
    passRate: number;
    totalFindings: number;
    criticalFindings: number;
  };
  groupedData: Record<string, {
    count: number;
    avgScore: number;
    totalFindings: number;
    criticalFindings: number;
    passRate: number;
  }>;
}

export function AuditAnalyticsPage() {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [quarterlyReport, setQuarterlyReport] = useState<QuarterlyReport | null>(null);
  const [error, setError] = useState('');
  const [reportError, setReportError] = useState('');
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  
  // Report filters
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [reportQuarter, setReportQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [reportGroupBy, setReportGroupBy] = useState<'auditor' | 'market' | 'region' | 'carrier'>('auditor');

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true); setError('');
    try {
      const response = await api.get('/safety-audits/analytics');
      const data = response.data.data;
      const metrics = ['totalAudits', 'completedAudits', 'avgScore', 'passRate', 'conditionalPassRate', 'failRate', 'totalFindings', 'openFindings', 'criticalFindings'];
      if (!data || metrics.some(key => !Number.isFinite(data[key])) || !Array.isArray(data.recentTrend)
        || ['byStatus', 'byRegion', 'byMarket', 'byCarrier', 'byAuditor'].some(key => !data[key] || typeof data[key] !== 'object')) {
        throw new Error('Unexpected analytics response');
      }
      setAnalytics(data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      setAnalytics(null);
      setError('Unable to load audit analytics. Please retry.');
    } finally {
      setLoading(false);
    }
  };

  const loadQuarterlyReport = async () => {
    setReportLoading(true);
    setReportError('');
    setQuarterlyReport(null);
    try {
      const response = await api.get('/safety-audits/reports/quarterly', {
        params: { year: reportYear, quarter: reportQuarter, groupBy: reportGroupBy },
      });
      setQuarterlyReport(response.data.data);
    } catch (error) {
      console.error('Error loading quarterly report:', error);
      setReportError('Unable to load the quarterly report. Please retry.');
    } finally {
      setReportLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-emerald-400';
    if (score >= 80) return 'text-green-400';
    if (score >= 70) return 'text-yellow-400';
    if (score >= 60) return 'text-orange-400';
    return 'text-red-400';
  };

  const getScoreBg = (score: number) => {
    if (score >= 90) return 'bg-emerald-500';
    if (score >= 80) return 'bg-green-500';
    if (score >= 70) return 'bg-yellow-500';
    if (score >= 60) return 'bg-orange-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  if (!analytics) return <main className="p-8"><Link to="/safety-audits">Back to audits</Link><p role="alert">{error}</p><button onClick={loadAnalytics}>Retry analytics</button></main>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="container mx-auto px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <Link to="/safety-audits" className="text-slate-400 hover:text-white">
                <ArrowLeft className="h-6 w-6" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <BarChart3 className="h-6 w-6 text-emerald-400" />
                  Audit Analytics
                </h1>
                <p className="text-sm text-slate-400">
                  Performance metrics and reporting dashboard
                </p>
              </div>
            </div>
            {/* S4: Export Report = client-side CSV of the grouped table. */}
            <button
              onClick={() => {
                if (!quarterlyReport) return;
                // External audit LOW-1: quote every field and neutralize
                // spreadsheet formula injection (=, +, -, @ prefixes).
                const safe = (v: unknown) => {
                  const s = String(v ?? '');
                  return `"${/^[=+\-@]/.test(s) ? `'${s}` : s}"`;
                };
                const rows = [
                  ['Group', 'Count', 'Avg Score %', 'Findings', 'Critical Findings', 'Pass Rate %'],
                  ['TOTAL', quarterlyReport.summary.totalAudits, quarterlyReport.summary.avgScore.toFixed(1), quarterlyReport.summary.totalFindings, quarterlyReport.summary.criticalFindings, quarterlyReport.summary.passRate.toFixed(0)],
                  ...Object.entries(quarterlyReport.groupedData).map(([group, d]) => [group, d.count, d.avgScore.toFixed(1), d.totalFindings, d.criticalFindings, d.passRate.toFixed(0)]),
                ];
                const csv = rows.map((r) => r.map(safe).join(',')).join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `audit-report-${quarterlyReport.quarter}-${quarterlyReport.year}.csv`;
                a.click();
                URL.revokeObjectURL(a.href);
              }}
              disabled={!quarterlyReport}
              className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 flex items-center gap-2 disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              Export Report
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8 space-y-8">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
            <div className="flex items-center gap-2 text-slate-400 text-sm mb-2">
              <FileText className="h-4 w-4" />
              Total Audits
            </div>
            <div className="text-3xl font-bold text-white">{analytics.totalAudits}</div>
            <div className="text-xs text-slate-400 mt-1">All recorded audits</div>
          </div>
          
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
            <div className="flex items-center gap-2 text-slate-400 text-sm mb-2">
              <Target className="h-4 w-4" />
              Avg Score
            </div>
            <div className={`text-3xl font-bold ${getScoreColor(analytics.avgScore)}`}>
              {analytics.avgScore.toFixed(1)}%
            </div>
            <div className="text-xs text-slate-500 mt-1">Target: 85%</div>
          </div>
          
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
            <div className="flex items-center gap-2 text-slate-400 text-sm mb-2">
              <CheckCircle className="h-4 w-4 text-green-400" />
              Pass Rate
            </div>
            <div className="text-3xl font-bold text-green-400">{analytics.passRate.toFixed(1)}%</div>
            <div className="text-xs text-slate-500 mt-1">{analytics.completedAudits} completed</div>
          </div>
          
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
            <div className="flex items-center gap-2 text-slate-400 text-sm mb-2">
              <AlertCircle className="h-4 w-4 text-yellow-400" />
              Conditional
            </div>
            <div className="text-3xl font-bold text-yellow-400">{analytics.conditionalPassRate.toFixed(1)}%</div>
            <div className="text-xs text-slate-500 mt-1">Needs follow-up</div>
          </div>
          
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
            <div className="flex items-center gap-2 text-slate-400 text-sm mb-2">
              <AlertTriangle className="h-4 w-4 text-red-400" />
              Findings
            </div>
            <div className="text-3xl font-bold text-white">{analytics.totalFindings}</div>
            <div className="text-xs text-orange-400 mt-1">{analytics.openFindings} open</div>
          </div>
          
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
            <div className="flex items-center gap-2 text-slate-400 text-sm mb-2">
              <XCircle className="h-4 w-4 text-red-400" />
              Critical
            </div>
            <div className="text-3xl font-bold text-red-400">{analytics.criticalFindings}</div>
            <div className="text-xs text-slate-500 mt-1">Immediate action</div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Score Trend */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-400" />
              Score Trend
            </h3>
            <div className="h-48 flex items-end gap-4 justify-around">
              {analytics.recentTrend.map((item, i) => (
                <div key={i} className="flex flex-col items-center gap-2">
                  <div className="text-sm text-slate-400">{item.avgScore}%</div>
                  <div
                    className={`w-16 rounded-t-lg ${getScoreBg(item.avgScore)} transition-all`}
                    style={{ height: `${item.avgScore * 1.5}px` }}
                  />
                  <div className="text-xs text-slate-500">{item.period}</div>
                  <div className="text-xs text-slate-400">{item.count} audits</div>
                </div>
              ))}
            </div>
          </div>

          {/* Pass/Fail Distribution */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <PieChart className="h-5 w-5 text-purple-400" />
              Pass/Fail Distribution
            </h3>
            <div className="flex items-center justify-center gap-8">
              <div className="relative w-40 h-40">
                <svg viewBox="0 0 100 100" className="transform -rotate-90">
                  <circle
                    cx="50" cy="50" r="40"
                    fill="none" stroke="#1e293b" strokeWidth="12"
                  />
                  <circle
                    cx="50" cy="50" r="40"
                    fill="none" stroke="#22c55e" strokeWidth="12"
                    strokeDasharray={`${analytics.passRate * 2.51} 251.2`}
                    strokeLinecap="round"
                  />
                  <circle
                    cx="50" cy="50" r="40"
                    fill="none" stroke="#eab308" strokeWidth="12"
                    strokeDasharray={`${analytics.conditionalPassRate * 2.51} 251.2`}
                    strokeDashoffset={`${-analytics.passRate * 2.51}`}
                    strokeLinecap="round"
                  />
                  <circle
                    cx="50" cy="50" r="40"
                    fill="none" stroke="#ef4444" strokeWidth="12"
                    strokeDasharray={`${analytics.failRate * 2.51} 251.2`}
                    strokeDashoffset={`${-(analytics.passRate + analytics.conditionalPassRate) * 2.51}`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-2xl font-bold text-white">{analytics.passRate.toFixed(0)}%</span>
                  <span className="text-xs text-slate-400">Pass Rate</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-slate-300">Pass: {analytics.passRate.toFixed(1)}%</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <span className="text-slate-300">Conditional: {analytics.conditionalPassRate.toFixed(1)}%</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span className="text-slate-300">Fail: {analytics.failRate.toFixed(1)}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Breakdown Tables */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* By Region */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <MapPin className="h-5 w-5 text-blue-400" />
              By Region
            </h3>
            <div className="space-y-3">
              {Object.entries(analytics.byRegion).map(([region, count]) => (
                <div key={region} className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white">{region}</span>
                      <span className="text-slate-400">{count}</span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full"
                        style={{ width: `${(count / Math.max(...Object.values(analytics.byRegion))) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* By Carrier */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Radio className="h-5 w-5 text-orange-400" />
              By Carrier
            </h3>
            <div className="space-y-3">
              {Object.entries(analytics.byCarrier).map(([carrier, count]) => (
                <div key={carrier} className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white">{carrier}</span>
                      <span className="text-slate-400">{count}</span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-orange-500 rounded-full"
                        style={{ width: `${(count / Math.max(...Object.values(analytics.byCarrier))) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* By Auditor */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-400" />
              By Auditor
            </h3>
            <div className="space-y-3">
              {Object.entries(analytics.byAuditor).map(([auditor, count]) => (
                <div key={auditor} className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white">{auditor}</span>
                      <span className="text-slate-400">{count}</span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-purple-500 rounded-full"
                        style={{ width: `${(count / Math.max(...Object.values(analytics.byAuditor))) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* By Market */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-teal-400" />
              By Market
            </h3>
            <div className="space-y-3">
              {Object.entries(analytics.byMarket).slice(0, 5).map(([market, count]) => (
                <div key={market} className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white">{market}</span>
                      <span className="text-slate-400">{count}</span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-teal-500 rounded-full"
                        style={{ width: `${(count / Math.max(...Object.values(analytics.byMarket))) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quarterly Report Generator */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-amber-400" />
            Quarterly Report Generator
          </h3>
          
          <div className="flex flex-wrap items-end gap-4 mb-6">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Year</label>
              <select
                value={reportYear}
                onChange={(e) => setReportYear(parseInt(e.target.value))}
                className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
              >
                {Array.from({ length: Math.max(3, new Date().getFullYear() - 2023 + 1) }, (_, i) => new Date().getFullYear() - i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Quarter</label>
              <select
                value={reportQuarter}
                onChange={(e) => setReportQuarter(parseInt(e.target.value))}
                className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
              >
                <option value={1}>Q1 (Jan-Mar)</option>
                <option value={2}>Q2 (Apr-Jun)</option>
                <option value={3}>Q3 (Jul-Sep)</option>
                <option value={4}>Q4 (Oct-Dec)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Group By</label>
              <select
                value={reportGroupBy}
                onChange={(e) => setReportGroupBy(e.target.value as any)}
                className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
              >
                <option value="auditor">Auditor</option>
                <option value="market">Market</option>
                <option value="region">Region</option>
                <option value="carrier">Carrier</option>
              </select>
            </div>
            <button
              onClick={loadQuarterlyReport}
              disabled={reportLoading}
              className="px-6 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg hover:from-amber-600 hover:to-orange-700 flex items-center gap-2"
            >
              {reportLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <BarChart3 className="h-4 w-4" />
              )}
              Generate Report
            </button>
          </div>

          {reportError && <p role="alert" className="text-red-400">{reportError}</p>}
          {quarterlyReport && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-5 gap-4 p-4 bg-slate-700/50 rounded-lg">
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">{quarterlyReport.summary.totalAudits}</div>
                  <div className="text-xs text-slate-400">Total Audits</div>
                </div>
                <div className="text-center">
                  <div className={`text-2xl font-bold ${getScoreColor(quarterlyReport.summary.avgScore)}`}>
                    {quarterlyReport.summary.avgScore.toFixed(1)}%
                  </div>
                  <div className="text-xs text-slate-400">Avg Score</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-400">{quarterlyReport.summary.passRate.toFixed(0)}%</div>
                  <div className="text-xs text-slate-400">Pass Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-400">{quarterlyReport.summary.totalFindings}</div>
                  <div className="text-xs text-slate-400">Findings</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-400">{quarterlyReport.summary.criticalFindings}</div>
                  <div className="text-xs text-slate-400">Critical</div>
                </div>
              </div>

              {/* Grouped Data Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">
                        {reportGroupBy.charAt(0).toUpperCase() + reportGroupBy.slice(1)}
                      </th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-400">Audits</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-400">Avg Score</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-400">Pass Rate</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-400">Findings</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-400">Critical</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(quarterlyReport.groupedData).map(([name, data]) => (
                      <tr key={name} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        <td className="py-3 px-4 text-white font-medium">{name}</td>
                        <td className="py-3 px-4 text-center text-slate-300">{data.count}</td>
                        <td className="py-3 px-4 text-center">
                          <span className={`font-medium ${getScoreColor(data.avgScore)}`}>
                            {data.avgScore.toFixed(1)}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={data.passRate >= 80 ? 'text-green-400' : data.passRate >= 60 ? 'text-yellow-400' : 'text-red-400'}>
                            {data.passRate.toFixed(0)}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center text-slate-300">{data.totalFindings}</td>
                        <td className="py-3 px-4 text-center">
                          {data.criticalFindings > 0 ? (
                            <span className="text-red-400 font-medium">{data.criticalFindings}</span>
                          ) : (
                            <span className="text-slate-500">0</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

